import { apiFetch } from './apiFetch';
import { requestError } from "./requestError";
import config from "../../config";
import { getPurchaseOrderKpis } from "./purchase.service";

const USE_BACKEND = true;

const EMPTY_FORM = {
    CompanyName: "", RegNo: "", TaxNo: "",
    Address: "", PhoneNumber: "", Email: "",
    Status: "Inactive", LogoUrl: "",
};

/* Get user from localStorage */
const getUser = () => JSON.parse(localStorage.getItem('user') || '{}');
// Same as the other services: the user id is not an account id, so there is
// no fallback to it.
const getAccountId = () => getUser().accountId;

/* The server's reason ({ error } or { message }), or the fallback */
const serverErrorMessage = async (res, fallback) => {
    if (res.status === 403) return "You don't have permission to change the company profile.";
    try {
        const body = await res.json();
        return body?.error || body?.message || fallback;
    } catch {
        return fallback;
    }
};

/**
 * Extracts and transforms profile data from server response
 */
function transformProfile(data) {
    if (data.LogoUrl && data.LogoUrl.startsWith('/')) {
        return { ...data, LogoUrl: `${config.apiUrl}${data.LogoUrl}` };
    }
    return data;
}

/**
 * Strips the apiUrl prefix for db storage
 */
function stripApiUrl(logoUrl) {
    if (!logoUrl) return "";
    return logoUrl.replace(config.apiUrl, "");
}

async function getProfile() {
    if (!USE_BACKEND) {
        return {
            CompanyName: "",
            RegNo: "", TaxNo: "",
            Address: "",
            PhoneNumber: "",
            Email: "",
            Status: "Active", LogoUrl: "",
            metrics: { customers: 0, suppliers: 0, invoices: 0, purchaseOrders: 0 }
        };
    }

    // The server reads the account from the login, so no account_id is sent.
    const res = await apiFetch(`${config.apiUrl}/getCompanyProfile`);
    if (!res.ok) throw new Error(await serverErrorMessage(res, `HTTP ${res.status}`));
    const data = await res.json();

    // Merge with EMPTY_FORM to ensure all fields exist for first-time use
    return transformProfile({ ...EMPTY_FORM, ...data });
}

async function uploadLogo(file) {
    const fd = new FormData();
    fd.append("files", file);
    const up = await apiFetch(`${config.apiUrl}/uploadCompanyLogo`, { method: 'POST', body: fd });
    if (!up.ok) throw new Error(await serverErrorMessage(up, "Logo upload failed."));
    const res = await up.json();
    return transformProfile(res); // returns { url: "..." } or similar
}

async function updateProfile(profile, logoFile) {
    if (!USE_BACKEND) {
        await new Promise(r => setTimeout(r, 600));
        return profile;
    }

    let logoUrl = profile.LogoUrl;

    // 1. Upload logo if new file provided
    if (logoFile) {
        const uploadRes = await uploadLogo(logoFile);
        logoUrl = uploadRes.LogoUrl || logoUrl;
    }

    // 2. Prepare payload (strip API URL prefix for DB)
    const savePayload = {
        ...profile,
        LogoUrl: stripApiUrl(logoUrl)
    };

    // 3. Update profile (the server sets account_id from the login)
    const res = await apiFetch(`${config.apiUrl}/updateCompanyProfile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(savePayload),
    });

    if (!res.ok) {
        // Keep `fields` ({ fieldName: message }) so the page can show each reason under its field.
        const error = await requestError(res);
        if (res.status === 403) error.message = "You don't have permission to change the company profile.";
        throw error;
    }

    // Use what the server saved (it trims the text), so the page shows the same.
    const saved = await res.json().catch(() => null);
    if (saved && typeof saved === "object") return transformProfile({ ...EMPTY_FORM, ...saved });
    return transformProfile({ ...profile, LogoUrl: logoUrl });
}

/* Total from a KPI request, or null when it can't be read (no permission for
   that module, or an error) — the card then shows "—" instead of a wrong 0. */
async function kpiTotal(request) {
    try {
        const res = await request;
        const kpi = typeof res?.json === "function"
            ? (res.ok ? await res.json() : null)
            : res; // getPurchaseOrderKpis already returns the parsed body
        const total = Number(kpi?.total);
        return kpi?.total != null && Number.isFinite(total) ? total : null;
    } catch {
        return null;
    }
}

async function getMetrics() {
    const accountId = getAccountId();
    // Each card on its own: one failing count doesn't blank the others.
    const [customers, suppliers, invoices, purchaseOrders] = await Promise.all([
        kpiTotal(apiFetch(`${config.apiUrl}/getCustomerKpis?account_id=${accountId}`)),
        kpiTotal(apiFetch(`${config.apiUrl}/getSupplierKpis?account_id=${accountId}`)),
        kpiTotal(apiFetch(`${config.apiUrl}/getInvoiceKpis?account_id=${accountId}`)),
        kpiTotal(getPurchaseOrderKpis()), // already handles account_id + auth internally
    ]);
    return { customers, suppliers, invoices, purchaseOrders };
}

export const companyProfileService = {
    getProfile,
    updateProfile,
    getMetrics
};